/**
 * The five tiles on Tracking → Delivery, built from whatever is readable.
 *
 * ⚠️ EVERY TILE DEGRADES ON ITS OWN. Tracking's three (plan, starts, clusters) come from the
 * Hiring Database; Prospects and Trends come from the warehouse, through two different endpoints.
 * Building the strip from a `data | null` rather than behind one early return is what keeps a
 * warehouse outage from blanking the delivery figures, and an HDB outage from blanking the
 * pipeline — the rule this tab already follows for the candidate funnel, applied to the strip.
 */
import type { TrackingData } from '../../../types/taTracking';
import type { OfferPulse } from '../../../types/openPositions';
import { offerTrend } from '../../../utils/offerPulse';
import { planningConsistency, clusterSpread, type StartAccuracy } from '../../../utils/deliveryTiles';
import type { Momentum, TrendShape } from '../../../utils/pipelineTrends';
import type { TileSpec } from './DeliveryTiles';
import { pctRaw } from './trackingUi';

const DASH = '—';

/**
 * ⚠️ WHAT A NOTE SAYS WHEN THE READ FAILED (CodeRabbit, PR #637). "No frozen baseline to compare
 * against" and "the Hiring Database could not be read" are opposite facts, and the first one
 * reassures. During an outage every HDB tile says the same true thing instead of three different
 * false ones — and none of them is toned `warn`, because an outage is not a bad trimester.
 */
const HDB_DOWN = 'the Hiring Database could not be read';

/**
 * ⚠️ PENDING, FAILED AND THIN ARE THREE STATES (CodeRabbit, PR #637). `usePipelineTrends` starts
 * with no data and ends with no data when it fails, so a tile built from the derived shape alone
 * said "not enough of the funnel is readable" for the first second of every page load and again
 * forever if the warehouse was down. Same rule as the HDB tiles below: a note must describe the
 * state it is actually in.
 */
export interface TrendsState {
  loading: boolean;
  error: string | null;
}

export function buildTiles({
  data,
  accuracy,
  hasBaseline,
  momentum,
  shape,
  trends,
  pulse,
}: {
  data: TrackingData | null;
  accuracy: StartAccuracy;
  /** Whether a plan was ever frozen. "No baseline" is not "no roles with a start". */
  hasBaseline: boolean;
  momentum: Momentum;
  shape: TrendShape;
  trends: TrendsState;
  pulse: OfferPulse | null | undefined;
}): TileSpec[] {
  const consistency = planningConsistency(data?.planVsNow);
  const clusters = data ? clusterSpread(data.clusters, data.pace.share) : null;
  const offers = offerTrend(pulse);

  return [
    {
      id: 'plan',
      label: 'Planning consistency',
      value: consistency.score == null ? DASH : pctRaw(consistency.score),
      note: !data
        ? HDB_DOWN
        : consistency.score == null
          ? 'no frozen baseline to compare against'
          : consistency.topCause
            ? `${consistency.churn} of ${consistency.plan} moved · mostly ${consistency.topCause.label}`
            : 'the plan has not moved',
      tone: !data || consistency.verdict === 'unknown' ? 'neutral' : consistency.verdict === 'solid' ? 'ok' : 'warn',
      why: 'How much of the FROZEN plan is still the plan: every role that was pushed, paused, dropped, undated, pulled in or added after the freeze, against the size of the plan. Roles that were FILLED are not counted as churn — that is the plan working. So this measures how well the trimester was planned, not how well it was delivered; Progress above measures delivery, and merging the two would leave neither answerable.',
    },
    {
      id: 'starts',
      label: 'Start date accuracy',
      value:
        accuracy.medianSlip == null
          ? DASH
          : accuracy.medianSlip <= 0
            ? 'On time'
            : `+${accuracy.medianSlip} ${accuracy.medianSlip === 1 ? 'month' : 'months'}`,
      note: !data
        ? HDB_DOWN
        : /*
           * ⚠️ NO FROZEN PLAN IS NOT "NO ROLES WITH A START" (CodeRabbit, PR #637) — the same
           * misattribution this PR already fixed for the HDB outage, one tile over. Accuracy is
           * measured over the roles the frozen plan is tracked against, so with no baseline there
           * is nothing to be accurate against, and saying "nothing has both a planned month and a
           * start" sends somebody to look at Airtable rows that are perfectly fine.
           */
          !hasBaseline
          ? 'no frozen plan to measure starts against'
          : accuracy.medianSlip == null
            ? 'nothing with both a planned month and a start'
            : `median across ${accuracy.measured} roles · ${accuracy.wayOff.length} three months or more out`,
      tone: !data || accuracy.verdict === 'unknown' ? 'neutral' : accuracy.verdict === 'on-time' ? 'ok' : 'warn',
      why: 'The median gap between the month a role was planned to start and where it actually sits. A role somebody has STARTED carries its final slip. One still OPEN past its month is measured to today and is still growing — leaving those out would flatter the figure exactly when the trimester is going badly. Measured in months because the Hiring Database stores a planned start month; weeks would be arithmetic on precision the source does not have.',
    },
    {
      id: 'prospects',
      label: 'Prospects',
      value:
        momentum.verdict === 'unknown'
          ? DASH
          : momentum.verdict === 'gaining'
            ? 'Gaining'
            : momentum.verdict === 'slowing'
              ? 'Slowing'
              : 'Holding',
      // The offer pulse is its OWN endpoint, so its note is preferred over the weekly series' state.
      note:
        pulse && offers.direction !== 'unknown'
          ? `${pulse.now} ${pulse.now === 1 ? 'offer' : 'offers'} out · ${offers.direction === 'up' ? 'more expected' : offers.direction === 'down' ? 'fewer expected' : 'steady'}`
          : trends.loading
            ? 'reading the weekly figures…'
            : trends.error
              ? 'weekly figures could not be read'
              : momentum.verdict === 'unknown'
                ? 'too few interviews and offers to read'
                : 'interviews and offers, last 2 weeks vs the 2 before',
      tone: momentum.verdict === 'slowing' ? 'warn' : momentum.verdict === 'gaining' ? 'ok' : 'neutral',
      why: 'Whether the stages that decide THIS trimester — first interviews, case interviews and offers — are running faster or slower than the fortnight before. Applications are deliberately not in it: an application today is a hire next quarter, and folding it in would let a good sourcing week mask offers drying up. That belongs to Trends. When something is slowing, the tile names the EARLIEST stage that is, not the worst — chasing a late stage while an earlier one starves it achieves nothing.',
    },
    {
      /*
       * ⚠️ THE VERDICT COMES FROM `trendShape`, NOT FROM `momentum` (CodeRabbit, PR #637). It read
       * `momentum.firstSlowing`, which only ever looks at first interviews, case interviews and
       * offers — so the one case this section exists for, applications falling while the late
       * stages still hold, put "Steady · nothing falling across the funnel" on the tile above a
       * section reading "The slowdown has not arrived yet". The tile and its own section have to
       * agree by construction, not by both happening to be written the same week.
       */
      id: 'trends',
      label: 'Trends',
      value: trends.loading || trends.error || shape.kind === 'unknown'
        ? DASH
        : shape.kind === 'steady'
          ? 'Steady'
          : 'Watch',
      note: trends.loading
        ? 'reading the weekly figures…'
        : trends.error
          ? 'weekly figures could not be read'
          : shape.kind === 'unknown'
            ? 'not enough of the funnel is readable'
            : shape.headline,
      tone:
        trends.loading || trends.error || shape.kind === 'unknown' || shape.kind === 'steady'
          ? 'neutral'
          : 'warn',
      why: 'Eight weeks of applications, first interviews, case interviews, offers made and offers accepted, each compared last two weeks against the two before. The section reads the PATTERN rather than the lines: everything down means the top of the funnel shrank, early stages holding while late ones fall means candidates are not getting through a stage, and early down while late still holds means the slowdown is on its way and has not arrived — the one worth catching, because everything still looks fine.',
    },
    {
      id: 'clusters',
      label: 'Clusters on pace',
      value: clusters && clusters.total > 0 ? `${clusters.onPace}/${clusters.total}` : DASH,
      note: !data
        ? HDB_DOWN
        : clusters?.worst
          ? `${clusters.worst.key} furthest behind at ${pctRaw(clusters.worst.progress)}`
          : 'no cluster has a target to measure',
      tone: !clusters || clusters.total === 0 ? 'neutral' : clusters.onPace === clusters.total ? 'ok' : 'warn',
      why: 'How many clusters are at or ahead of the share of the trimester the calendar has used up, and which one is furthest behind. It names the WORST rather than averaging the three: a company on pace with one cluster at 40% is not a company on pace, and capacity does not pool between clusters. Each cluster is measured against its own live target, not against the frozen plan — a CSV baseline carries the cluster column only if whoever exported it included it.',
    },
  ];
}
